import { useState } from 'react';
import clsx from 'clsx';
import { planNalevering, type NaleveringRichting } from '../../lib/nalevering';
import type { OrderItem } from '../../lib/orders';

interface NaleveringDialogProps {
  orderId: string;
  orderNumber: string;
  /** Welke stop wordt gecorrigeerd. Bepaalt gate, doelstatus en teksten. */
  richting: NaleveringRichting;
  /** Primaire kleden van de order die voor deze richting kiesbaar zijn (de caller filtert). */
  items: OrderItem[];
  onClose: () => void;
  onSaved: () => void;
}

/** Teksten per richting; de regels zelf staan in api/orders/_nalevering.ts. */
const TEKST: Record<
  NaleveringRichting,
  { titel: string; uitleg: JSX.Element; leeg: string; placeholder: string; knop: string; fout: string }
> = {
  afleveren: {
    titel: 'Niet afgeleverd, nalevering plannen',
    uitleg: (
      <>
        Vink de kleden aan die <span className="font-medium">niet</span> bij de klant zijn afgeleverd.
        Die gaan terug naar <span className="font-medium">Ingepakt</span> (in het schap, opnieuw
        klaarleggen en inladen), de order gaat naar{' '}
        <span className="font-medium">Terugbrengen plannen</span> (handmatig, geen klantmail) en je
        plant daarna een nieuwe afleverrit. De factuur blijft staan.
      </>
    ),
    leeg: "Geen kleden op 'Afgeleverd' op deze order.",
    placeholder: 'Bv. kleed vergeten op de route, ligt nog in het schap',
    knop: 'terugnemen',
    fout: 'Nalevering mislukt',
  },
  ophalen: {
    titel: 'Niet opgehaald, na-ophaal plannen',
    uitleg: (
      <>
        Vink de kleden aan die <span className="font-medium">niet</span> zijn meegenomen bij de
        klant. Die gaan terug naar vóór-ophaal, de order gaat naar{' '}
        <span className="font-medium">Ophalen plannen</span> (handmatig, geen klantmail) en je plant
        daarna een nieuwe ophaalrit. De andere kleden blijven gewoon in de wasserij.
      </>
    ),
    leeg: "Geen kleden op 'Opgehaald' of 'Klaar voor Voorinspectie' op deze order.",
    placeholder: 'Bv. meubel stond erop, klant was niet thuis voor het tweede kleed',
    knop: 'als niet opgehaald markeren',
    fout: 'Na-ophaal mislukt',
  },
};

function afmeting(item: OrderItem): string | null {
  if (item.diameterCm) return `Ø ${Math.round(item.diameterCm)} cm`;
  if (item.lengthCm && item.widthCm) return `${Math.round(item.lengthCm)} x ${Math.round(item.widthCm)} cm`;
  return null;
}

/**
 * Achteraf-correctie: kantoor ontdekt dat een kleed niet mee is gegaan terwijl
 * de stop als klaar is gemeld. Aflever- en ophaalkant delen deze dialoog.
 */
export function NaleveringDialog({ orderId, orderNumber, richting, items, onClose, onSaved }: NaleveringDialogProps) {
  const t = TEKST[richting];
  const kiesbaar = items;
  const [gekozen, setGekozen] = useState<Set<string>>(new Set());
  const [notitie, setNotitie] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [waarschuwing, setWaarschuwing] = useState<string | null>(null);

  const toggle = (id: string) =>
    setGekozen((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const canSubmit = !submitting && gekozen.size > 0;

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const result = await planNalevering({
        orderId,
        itemIds: Array.from(gekozen),
        richting,
        notitie: notitie.trim() || undefined,
      });
      if (!result.ok) {
        setError(result.error || t.fout);
        return;
      }
      if (result.teruggeduwd) {
        // Niet stil laten passeren: de order staat dan niet op de verwachte status.
        setWaarschuwing(
          `Kleden zijn teruggezet, maar de order sprong naar '${result.nieuweStatus}'. Zet de status handmatig op '${result.verwachteStatus}' en meld dit.`
        );
        return;
      }
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : t.fout);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        <div className="px-6 py-4 border-b border-slate-200">
          <h2 className="text-base font-semibold text-slate-900">{t.titel}</h2>
          <p className="text-xs text-slate-500 mt-1">Order {orderNumber}</p>
        </div>

        <div className="px-6 py-4 space-y-3">
          <p className="text-sm text-slate-700">{t.uitleg}</p>

          {kiesbaar.length === 0 ? (
            <p className="text-sm text-amber-700">{t.leeg}</p>
          ) : (
            <div className="space-y-1.5">
              {kiesbaar.map((item) => {
                const aan = gekozen.has(item.id);
                const maat = afmeting(item);
                return (
                  <label
                    key={item.id}
                    className={clsx(
                      'flex items-start gap-2 rounded border px-3 py-2 cursor-pointer',
                      aan ? 'border-emerald-500 bg-emerald-50' : 'border-slate-300 hover:border-slate-400'
                    )}
                  >
                    <input
                      type="checkbox"
                      checked={aan}
                      onChange={() => toggle(item.id)}
                      disabled={submitting}
                      className="mt-0.5"
                    />
                    <span>
                      <span className="block text-sm text-slate-900">{item.itemNumber}</span>
                      <span className="block text-xs text-slate-500">
                        {item.name}
                        {maat ? ` · ${maat}` : ''}
                      </span>
                    </span>
                  </label>
                );
              })}
            </div>
          )}

          <label className="block">
            <span className="text-sm text-slate-700">Opmerking (optioneel)</span>
            <textarea
              value={notitie}
              onChange={(e) => setNotitie(e.target.value)}
              rows={2}
              className="mt-1 w-full border border-slate-300 rounded px-2 py-1.5 text-sm"
              placeholder={t.placeholder}
              disabled={submitting}
            />
          </label>

          {error && <p className="text-xs text-red-600">{error}</p>}
          {waarschuwing && <p className="text-xs text-amber-700">{waarschuwing}</p>}
        </div>

        <div className="px-6 py-3 border-t border-slate-200 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={waarschuwing ? onSaved : onClose}
            disabled={submitting}
            className="text-sm text-slate-600 hover:text-slate-900 px-3 py-1.5"
          >
            Sluiten
          </button>
          {!waarschuwing && (
            <button
              type="button"
              onClick={handleSubmit}
              disabled={!canSubmit}
              className={clsx(
                'text-sm font-medium px-3 py-1.5 rounded',
                !canSubmit
                  ? 'bg-slate-200 text-slate-500 cursor-not-allowed'
                  : 'bg-emerald-600 text-white hover:bg-emerald-700'
              )}
            >
              {submitting
                ? 'Bezig…'
                : `${gekozen.size || ''} ${gekozen.size === 1 ? 'kleed' : 'kleden'} ${t.knop}`.trim()}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
